import React, { useState, useEffect } from 'react';
import './ColaboradorDashboard.css';
import { FaCheckCircle, FaExclamationCircle, FaSpinner, FaClock, FaClipboardList, FaTasks, FaChartLine, FaProjectDiagram, FaCheck, FaTimes, FaInfoCircle, FaUserCircle, FaCalendar, FaHourglassHalf } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { fetchAuth } from '../../utils/api';
import TaskModal from '../../components/TaskModal/TaskModal';

const StatusTag = ({ estado }) => {
  let className = 'status-tag';
  let icon = null;
  
  // Mapear los estados de la base de datos
  const estadoUpper = estado?.toUpperCase().replace(' ', '_');
  
  switch (estadoUpper) {
    case 'COMPLETADO': className += ' aprobado'; icon = <FaCheckCircle />; break;
    case 'EN_REVISIÓN': 
    case 'EN_REVISION': className += ' en-revision'; icon = <FaSpinner />; break;
    case 'PENDIENTE': className += ' pendiente'; icon = <FaClock />; break;
    case 'EN_PROGRESO': className += ' en-progreso'; icon = <FaSpinner />; break;
    case 'PAUSADO': className += ' pausado'; icon = <FaClock />; break;
    case 'CANCELADO': className += ' cancelado'; icon = <FaExclamationCircle />; break;
    default: className += ' pendiente'; icon = <FaClock />; break;
  }
  return <span className={className}>{icon} {estado}</span>;
};

export default function ColaboradorDashboard() {
  const [tasks, setTasks] = useState([]);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [confirmRejectTask, setConfirmRejectTask] = useState(null);
  const [confirmRejectLoading, setConfirmRejectLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskOnlyInfo, setSelectedTaskOnlyInfo] = useState(false);
  const [selectedTaskHidePlazo, setSelectedTaskHidePlazo] = useState(false);
  const [projects, setProjects] = useState({ solicitudes: [], aceptados: [] });
  const [projectDetail, setProjectDetail] = useState(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [solPlazo, setSolPlazo] = useState([]);
  const [solLoading, setSolLoading] = useState(false);
  const [solError, setSolError] = useState(null);
  const [solTarea, setSolTarea] = useState([]);
  const [toast, setToast] = useState({ visible: false, type: 'success', title: '', message: '' });
  const [progresoTab, setProgresoTab] = useState('tareas');

  useEffect(() => {
    fetchTasks();
    fetchProjects();
    fetchSolicitudesPlazo();
    fetchSolicitudesTarea();
    const id = setInterval(() => {
      fetchSolicitudesPlazo();
      fetchSolicitudesTarea();
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const normalizeEstado = (e) => {
    const s = String(e || '').trim();
    const noAccents = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return noAccents.toUpperCase().replace(/\s+/g, '_');
  };

  const canonEstado = (e) => {
    switch (normalizeEstado(e)) {
      case 'EN_PROGRESO': return 'En Progreso';
      case 'EN_REVISION': return 'En Revisión';
      case 'PENDIENTE': return 'Pendiente';
      case 'COMPLETADO': return 'Completado';
      case 'APROBADO': return 'Aprobado';
      default: return String(e || '').trim() || 'Pendiente';
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAuth('/api/mis-tareas/');
      const tareasArray = data.tareas || data || [];
      const normalized = (Array.isArray(tareasArray) ? tareasArray : []).map(t => ({
        ...t,
        estado: canonEstado(t.estado)
      }));
      setTasks(normalized);
      setRejectedCount(parseInt(data.rechazadas_count || 0, 10));
    } catch (err) {
      console.error('❌ Error al cargar tareas:', err);
      setError(err.message || 'Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await fetchAuth('/api/colaborador/mis-proyectos/');
      const aceptados = Array.isArray(data.aceptados)
        ? data.aceptados
        : (Array.isArray(data.proyectos) ? data.proyectos : []);
      const solicitudes = Array.isArray(data.solicitudes) ? data.solicitudes : [];
      setProjects({ solicitudes, aceptados });
    } catch (err) {
      console.error('Error cargando proyectos:', err);
    }
  };

  const fetchSolicitudesPlazo = async () => {
    try {
      setSolLoading(true);
      setSolError(null);
      const data = await fetchAuth('/api/colaborador/solicitudes-plazo/');
      setSolPlazo(Array.isArray(data.solicitudes) ? data.solicitudes : []);
    } catch (err) {
      console.error('Error cargando solicitudes de plazo:', err);
      setSolError(err.message || 'No se pudieron cargar las solicitudes de plazo');
    } finally {
      setSolLoading(false);
    }
  };

  const fetchSolicitudesTarea = async () => {
    try {
      setSolLoading(true);
      setSolError(null);
      const data = await fetchAuth('/api/colaborador/solicitudes-tarea/');
      setSolTarea(Array.isArray(data.solicitudes) ? data.solicitudes : []);
    } catch (err) {
      console.error('Error cargando solicitudes de tarea:', err);
      setSolError(err.message || 'No se pudieron cargar las solicitudes de tarea');
    } finally {
      setSolLoading(false);
    }
  };

  useEffect(() => {
    try {
      const seenArr = JSON.parse(localStorage.getItem('cf_seen_plazo_notifs') || '[]');
      const seen = new Set(Array.isArray(seenArr) ? seenArr : []);
      const responded = (Array.isArray(solPlazo) ? solPlazo : []).filter(s => {
        const est = String(s.estado || '').toUpperCase();
        return s.fecha_respuesta && est !== 'PENDIENTE';
      });
      for (const s of responded) {
        const key = `${s.id || s.id_tarea}-${s.fecha_respuesta || s.comentario_jefe || ''}`;
        if (!seen.has(key)) {
          const estUp = String(s.estado || '').toUpperCase();
          const isApproved = estUp === 'APROBADO';
          setToast({
            visible: true,
            type: isApproved ? 'success' : 'error',
            title: isApproved ? 'Solicitud de plazo aprobada' : 'Solicitud de plazo rechazada',
            message: `${s.tarea_nombre || 'Tarea'}${s.comentario_jefe ? ` · ${s.comentario_jefe}` : ''}`
          });
          if (isApproved) {
            try { setProgresoTab('tareas'); } catch {}
            try { fetchTasks(); } catch {}
          }
          seen.add(key);
          localStorage.setItem('cf_seen_plazo_notifs', JSON.stringify(Array.from(seen)));
          break;
        }
      }
    } catch {}
  }, [solPlazo]);

  useEffect(() => {
    if (toast.visible) {
      const t = setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 5000);
      return () => clearTimeout(t);
    }
  }, [toast.visible]);

  const openProjectDetail = async (id) => {
    try {
      const d = await fetchAuth(`/api/colaborador/proyectos/${id}/detalle/`);
      const flattened = d && d.proyecto
        ? { ...d.proyecto, tareas_asignadas: Array.isArray(d.tareas) ? d.tareas : [] }
        : d;
      setProjectDetail(flattened);
      setProjectDetailOpen(true);
    } catch {}
  };

  const findProjectIdByName = (name) => {
    const norm = (s) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const n = norm(name);
    const fromAccepted = (Array.isArray(projects?.aceptados) ? projects.aceptados : []).find(p => norm(p.nombre_proyecto) === n);
    if (fromAccepted) return fromAccepted.id_proyecto;
    const fromSolicitudes = (Array.isArray(projects?.solicitudes) ? projects.solicitudes : []).find(p => norm(p.nombre_proyecto) === n);
    return fromSolicitudes ? fromSolicitudes.id_proyecto : null;
  };

  const openSolicitudProjectDetail = async (sol, tmatch) => {
    const proyectoNombre = typeof tmatch?.proyecto === 'object' ? (tmatch?.proyecto?.nombre || sol?.proyecto_nombre || '') : (tmatch?.proyecto || sol?.proyecto_nombre || '');
    const fallback = tmatch || { id_tarea: sol.id_tarea, nombre: sol.tarea_nombre, estado: 'Pendiente', proyecto: proyectoNombre };
    setSelectedTaskOnlyInfo(true);
    setSelectedTask(fallback);
  };

  const handleAcceptTask = async (task) => {
    try {
      await fetchAuth('/api/colaborador/tareas/aceptar/', { method: 'POST', body: JSON.stringify({ id_tarea: task.id_tarea }) });
      await fetchTasks();
      await fetchProjects();
    } catch (err) {
      setError(err.message || 'Error al aceptar la tarea');
    }
  };

  const handleRejectTask = async (task) => {
    try {
      setConfirmRejectLoading(true);
      await fetchAuth('/api/colaborador/tareas/rechazar/', { method: 'POST', body: JSON.stringify({ id_tarea: task.id_tarea }) });
      await fetchTasks();
      await fetchProjects();
      setConfirmRejectOpen(false);
      setConfirmRejectTask(null);
    } catch (err) {
      setError(err.message || 'Error al rechazar la tarea');
    } finally {
      setConfirmRejectLoading(false);
    }
  };

  const openConfirmReject = (task) => {
    setConfirmRejectTask(task);
    setConfirmRejectOpen(true);
  };

  const stats = {
    total: Array.isArray(tasks) ? tasks.length : 0,
    completadas: Array.isArray(tasks) ? tasks.filter(t => normalizeEstado(t.estado) === 'COMPLETADO').length : 0,
    enRevision: Array.isArray(tasks) ? tasks.filter(t => normalizeEstado(t.estado) === 'EN_REVISION').length : 0,
    enProgreso: Array.isArray(tasks) ? tasks.filter(t => normalizeEstado(t.estado) === 'EN_PROGRESO').length : 0,
    pendientes: Array.isArray(tasks) ? tasks.filter(t => normalizeEstado(t.estado) === 'PENDIENTE').length : 0,
    rechazadas: rejectedCount,
    proyectosAceptados: Array.isArray(projects?.aceptados) ? projects.aceptados.length : 0,
    solicitudesPendientes: Array.isArray(solTarea) ? solTarea.filter(s => String(s.estado || '').toUpperCase() === 'PENDIENTE').length : 0,
  };

  const kpis = {
    completionRate: stats.total > 0 ? stats.completadas / stats.total : 0,
    pendingRate: stats.total > 0 ? stats.pendientes / stats.total : 0,
    reviewRate: stats.total > 0 ? stats.enRevision / stats.total : 0,
    progressRate: stats.total > 0 ? stats.enProgreso / stats.total : 0,
    acceptedRate:
      (Array.isArray(projects?.aceptados) || Array.isArray(projects?.solicitudes))
        ? ((projects.aceptados?.length || 0) / (((projects.aceptados?.length || 0) + (projects.solicitudes?.length || 0)) || 1))
        : 0,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
          <p className="text-slate-600 text-lg font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-800 text-xl font-bold mb-2">Error al cargar</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchTasks}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-6">
      {toast.visible && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`flex items-start gap-3 rounded-xl border shadow-lg px-4 py-3 ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className={`${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'} text-xl`}>
              {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800">{toast.title}</p>
              <p className="text-sm text-slate-600">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(prev => ({ ...prev, visible: false }))}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Cerrar notificación"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg">
              <FaUserCircle className="text-white text-3xl" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
                Panel de Colaborador
              </h1>
              <p className="text-slate-600 mt-1">
                Gestiona tus tareas y monitorea tu progreso
              </p>
            </div>
          </div>
        </div>

        {/* Estadísticas Principales */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2.5 rounded-xl">
                <FaClipboardList className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.total}</h3>
            <p className="text-slate-600 text-xs mt-1">Total Tareas</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-2.5 rounded-xl">
                <FaCheckCircle className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.completadas}</h3>
            <p className="text-slate-600 text-xs mt-1">Completadas</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-2.5 rounded-xl">
                <FaSpinner className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.enRevision}</h3>
            <p className="text-slate-600 text-xs mt-1">En Revisión</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-slate-500 to-slate-600 p-2.5 rounded-xl">
                <FaClock className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.pendientes}</h3>
            <p className="text-slate-600 text-xs mt-1">Pendientes</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-2.5 rounded-xl">
                <FaTasks className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.enProgreso}</h3>
            <p className="text-slate-600 text-xs mt-1">En Progreso</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-2.5 rounded-xl">
                <FaProjectDiagram className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.proyectosAceptados}</h3>
            <p className="text-slate-600 text-xs mt-1">Proyectos</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-gradient-to-br from-red-500 to-red-600 p-2.5 rounded-xl">
                <FaTimes className="text-white text-xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-slate-800">{stats.rechazadas}</h3>
            <p className="text-slate-600 text-xs mt-1">Rechazadas</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FaChartLine className="text-blue-600" />
            Indicadores de Rendimiento (KPI)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Tasa de Finalización</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(kpis.completionRate * 100)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.round(kpis.completionRate * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Tareas Pendientes</span>
                <span className="text-sm font-bold text-slate-600">{Math.round(kpis.pendingRate * 100)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-slate-400 to-slate-500 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.round(kpis.pendingRate * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">En Revisión</span>
                <span className="text-sm font-bold text-amber-600">{Math.round(kpis.reviewRate * 100)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-amber-600 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.round(kpis.reviewRate * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Proyectos Aceptados</span>
                <span className="text-sm font-bold text-indigo-600">{Math.round(kpis.acceptedRate * 100)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.round(kpis.acceptedRate * 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Solicitudes de Plazo Pendientes</span>
                <span className="text-sm font-bold text-amber-600">{stats.solicitudesPendientes}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-amber-600 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, stats.solicitudesPendientes * 10)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Solicitudes de Tarea (tabla con acciones) */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <FaHourglassHalf className="text-amber-600" />
              Solicitudes de Tarea
            </h3>
            {!solLoading && solTarea.length > 0 && (
              <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">{solTarea.length}</span>
            )}
          </div>
          {solError && (solTarea.length === 0) && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{solError}</div>
          )}
          {(!solLoading && solTarea.length === 0) ? (
            <div className="text-center py-12 text-slate-500">
              <FaClipboardList className="text-5xl mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tienes solicitudes de tarea</p>
              <p className="text-sm mt-1">Cuando te asignen tareas, aparecerán aquí para aceptar o rechazar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre Tarea</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Proyecto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Solicitud</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {solTarea.map(s => {
                    const tmatch = Array.isArray(tasks) ? tasks.find(t => String(t.id_tarea) === String(s.id_tarea)) : null;
                    const proyectoNombre = typeof tmatch?.proyecto === 'object' ? (tmatch?.proyecto?.nombre || s.proyecto_nombre || '—') : (tmatch?.proyecto || s.proyecto_nombre || '—');
                    return (
                      <tr key={`${s.id}-${s.id_tarea}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium text-slate-800">{s.tarea_nombre || `Tarea #${s.id_tarea}`}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{proyectoNombre}</td>
                        <td className="py-3 px-4">
                          <StatusTag estado={'Pendiente'} />
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {s.fecha_solicitud ? (
                            <div className="flex items-center gap-1">
                              <FaCalendar className="text-xs" />
                              {new Date(s.fecha_solicitud).toLocaleDateString('es-ES')}
                            </div>
                          ) : 'Sin fecha'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2 flex-nowrap">
                            <button
                              onClick={() => handleAcceptTask({ id_tarea: s.id_tarea })}
                              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                            >
                              <FaCheck className="text-xs" />
                              Aceptar
                            </button>
                            <button
                              onClick={() => openConfirmReject({ id_tarea: s.id_tarea, nombre: s.tarea_nombre })}
                              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                            >
                              <FaTimes className="text-xs" />
                              Rechazar
                            </button>
                            <button 
                              onClick={() => openSolicitudProjectDetail(s, tmatch)}
                              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                            >
                              <FaInfoCircle className="text-xs" />
                              Ver Detalle
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tareas Corregidas (En Progreso) */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <FaTasks className="text-purple-600" />
                Tareas en Progreso
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setProgresoTab('tareas')}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    progresoTab === 'tareas' ? 'bg-purple-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <FaTasks className="text-xs" />
                  Tareas
                </button>
                <button
                  onClick={() => setProgresoTab('plazos')}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    progresoTab === 'plazos' ? 'bg-amber-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <FaHourglassHalf className="text-xs" />
                  Solicitudes de Plazo
                </button>
              </div>
            </div>
            {Array.isArray(tasks) && tasks.filter(t => normalizeEstado(t.estado) === 'EN_PROGRESO').length > 0 && progresoTab === 'tareas' && (
              <span className="bg-purple-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                {tasks.filter(t => normalizeEstado(t.estado) === 'EN_PROGRESO').length}
              </span>
            )}
            {Array.isArray(solPlazo) && solPlazo.length > 0 && progresoTab === 'plazos' && (
              <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                {solPlazo.length}
              </span>
            )}
          </div>

          {progresoTab === 'tareas' && (Array.isArray(tasks) && tasks.filter(t => normalizeEstado(t.estado) === 'EN_PROGRESO').length === 0) ? (
            <div className="text-center py-12 text-slate-500">
              <FaCheckCircle className="text-5xl mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tienes tareas en progreso</p>
              <p className="text-sm mt-1">Cuando envíes tareas, aparecerán aquí para seguimiento</p>
            </div>
          ) : progresoTab === 'tareas' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre Tarea</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Proyecto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Límite</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha de Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.filter(t => normalizeEstado(t.estado) === 'EN_PROGRESO').map(task => (
                    <tr key={task.id_tarea} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{task.nombre}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{task.proyecto?.nombre || 'Sin proyecto'}</td>
                      <td className="py-3 px-4">
                        <StatusTag estado={task.estado} />
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio) ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio)}
                          </div>
                        ) : (
                          'Sin fecha'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {task.fecha_plazo ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_plazo)}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const ep = String(task.estado_plazo || '').toUpperCase();
                          const cls = ep === 'APROBADO' ? 'bg-emerald-100 text-emerald-700' : ep === 'RECHAZADO' ? 'bg-red-100 text-red-700' : ep === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
                          const label = task.estado_plazo ? (ep === 'APROBADO' ? 'Plazo aceptado' : ep === 'RECHAZADO' ? 'Plazo rechazado' : 'Pendiente') : '-';
                          const ago = (() => {
                            const d = task.fecha_plazo_solicitud ? new Date(task.fecha_plazo_solicitud) : null;
                            if (!d || ep !== 'PENDIENTE') return null;
                            const diffMs = Date.now() - d.getTime();
                            const diffMin = Math.floor(diffMs / 60000);
                            if (diffMin < 60) return `${diffMin} min`;
                            const diffH = Math.floor(diffMin / 60);
                            if (diffH < 24) return `${diffH} h`;
                            const diffD = Math.floor(diffH / 24);
                            return `${diffD} días`;
                          })();
                          return (
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${cls}`}>
                              {label}{ago ? ` · hace ${ago}` : ''}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <button 
                          onClick={() => { setSelectedTaskHidePlazo(false); setSelectedTaskOnlyInfo(false); setSelectedTask(task); }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                        >
                          <FaInfoCircle className="text-xs" />
                          Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Tarea</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha solicitada</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha respuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(solPlazo) ? solPlazo : []).map(s => {
                    const est = String(s.estado || '').toUpperCase();
                    const badge = est === 'PENDIENTE' ? 'bg-slate-100 text-slate-700' : est === 'APROBADO' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700';
                    const label = est === 'APROBADO' ? 'Plazo aceptado' : est === 'RECHAZADO' ? 'Plazo rechazado' : 'Pendiente';
                    const matchedTask = (Array.isArray(tasks) ? tasks : []).find(t => t.id_tarea === s.id_tarea);
                    const fallbackTask = { id_tarea: s.id_tarea, nombre: s.tarea_nombre, estado: est === 'APROBADO' ? 'En Progreso' : 'En Revisión', proyecto: matchedTask?.proyecto };
                    return (
                      <tr key={s.id || s.id_solicitud || `${s.id_tarea}-${s.fecha_solicitud || ''}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-slate-800">{s.tarea_nombre || s.tarea || '—'}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${badge}`}>{label}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.fecha_solicitud ? formatDate(s.fecha_solicitud) : '—'}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{s.fecha_respuesta ? formatDate(s.fecha_respuesta) : '—'}</td>
                      </tr>
                    );
                  })}
                  {Array.isArray(solPlazo) && solPlazo.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500">
                        No hay solicitudes de plazo
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tareas en Revisión */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <FaSpinner className="text-amber-600" />
              Tareas en Revisión
            </h3>
            {Array.isArray(tasks) && tasks.filter(t => normalizeEstado(t.estado) === 'EN_REVISION').length > 0 && (
              <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                {tasks.filter(t => normalizeEstado(t.estado) === 'EN_REVISION').length}
              </span>
            )}
          </div>

          {Array.isArray(tasks) && tasks.filter(t => normalizeEstado(t.estado) === 'EN_REVISION').length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FaCheckCircle className="text-5xl mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tienes tareas en revisión</p>
              <p className="text-sm mt-1">Cuando envíes tareas, aparecerán aquí</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre Tarea</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Proyecto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Límite</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha de Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.filter(t => normalizeEstado(t.estado) === 'EN_REVISION').map(task => (
                    <tr key={task.id_tarea} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{task.nombre}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{task.proyecto?.nombre || 'Sin proyecto'}</td>
                      <td className="py-3 px-4">
                        <StatusTag estado={task.estado} />
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio) ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio)}
                          </div>
                        ) : (
                          'Sin fecha'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {task.fecha_plazo ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_plazo)}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const ep = String(task.estado_plazo || '').toUpperCase();
                          const cls = ep === 'APROBADO' ? 'bg-emerald-100 text-emerald-700' : ep === 'RECHAZADO' ? 'bg-red-100 text-red-700' : ep === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
                          const label = task.estado_plazo ? (ep === 'APROBADO' ? 'Plazo aceptado' : ep === 'RECHAZADO' ? 'Plazo rechazado' : 'Pendiente') : '-';
                          const d = task.fecha_plazo_solicitud ? new Date(task.fecha_plazo_solicitud) : null;
                          let ago = null;
                          if (d && ep === 'PENDIENTE') {
                            const diffMs = Date.now() - d.getTime();
                            const diffMin = Math.floor(diffMs / 60000);
                            if (diffMin < 60) ago = `${diffMin} min`;
                            else {
                              const diffH = Math.floor(diffMin / 60);
                              if (diffH < 24) ago = `${diffH} h`;
                              else ago = `${Math.floor(diffH / 24)} días`;
                            }
                          }
                          return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${cls}`}>{label}{ago ? ` · hace ${ago}` : ''}</span>;
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <button 
                          onClick={() => { setSelectedTaskHidePlazo(false); setSelectedTaskOnlyInfo(true); setSelectedTask(task); }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                        >
                          <FaInfoCircle className="text-xs" />
                          Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Tareas Aprobadas */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <FaCheckCircle className="text-emerald-600" />
              Tareas Aprobadas
            </h3>
            {Array.isArray(tasks) && tasks.filter(t => ['COMPLETADO','APROBADO'].includes(normalizeEstado(t.estado))).length > 0 && (
              <span className="bg-emerald-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                {tasks.filter(t => ['COMPLETADO','APROBADO'].includes(normalizeEstado(t.estado))).length}
              </span>
            )}
          </div>

          {Array.isArray(tasks) && tasks.filter(t => ['COMPLETADO','APROBADO'].includes(normalizeEstado(t.estado))).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FaCheckCircle className="text-5xl mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tienes tareas aprobadas</p>
              <p className="text-sm mt-1">Cuando sean aprobadas, aparecerán aquí</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Nombre Tarea</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Proyecto</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha Límite</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha de Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado Plazo</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.filter(t => ['COMPLETADO','APROBADO'].includes(normalizeEstado(t.estado))).map(task => (
                    <tr key={task.id_tarea} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{task.nombre}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{task.proyecto?.nombre || 'Sin proyecto'}</td>
                      <td className="py-3 px-4">
                        <StatusTag estado={task.estado} />
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio) ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_entrega_inicial || task.fecha_termino || task.fecha_inicio)}
                          </div>
                        ) : (
                          'Sin fecha'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {task.fecha_plazo ? (
                          <div className="flex items-center gap-1">
                            <FaCalendar className="text-xs" />
                            {formatDate(task.fecha_plazo)}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const ep = String(task.estado_plazo || '').toUpperCase();
                          const cls = ep === 'APROBADO' ? 'bg-emerald-100 text-emerald-700' : ep === 'RECHAZADO' ? 'bg-red-100 text-red-700' : ep === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
                          const label = task.estado_plazo ? (ep === 'APROBADO' ? 'Plazo aceptado' : ep === 'RECHAZADO' ? 'Plazo rechazado' : 'Pendiente') : '-';
                          const d = task.fecha_plazo_solicitud ? new Date(task.fecha_plazo_solicitud) : null;
                          let ago = null;
                          if (d && ep === 'PENDIENTE') {
                            const diffMs = Date.now() - d.getTime();
                            const diffMin = Math.floor(diffMs / 60000);
                            if (diffMin < 60) ago = `${diffMin} min`;
                            else {
                              const diffH = Math.floor(diffMin / 60);
                              if (diffH < 24) ago = `${diffH} h`;
                              else ago = `${Math.floor(diffH / 24)} días`;
                            }
                          }
                          return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${cls}`}>{label}{ago ? ` · hace ${ago}` : ''}</span>;
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <button 
                          onClick={() => { setSelectedTaskOnlyInfo(true); setSelectedTask(task); }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                        >
                          <FaInfoCircle className="text-xs" />
                          Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Tarea */}
      {selectedTask && (
        <TaskModal 
          task={selectedTask}
          onClose={() => { setSelectedTaskHidePlazo(false); setSelectedTaskOnlyInfo(false); setSelectedTask(null); }}
          onTaskUpdated={fetchTasks}
          onlyInfo={selectedTaskOnlyInfo}
          hidePlazo={selectedTaskHidePlazo}
        />
      )}
      {confirmRejectOpen && confirmRejectTask && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full border border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <FaExclamationCircle className="text-amber-600" />
              <h4 className="text-slate-800 font-bold">Confirmar rechazo</h4>
            </div>
            <p className="text-slate-600 mb-4">¿Seguro rechazar la tarea "{confirmRejectTask.nombre}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => { if (!confirmRejectLoading) { setConfirmRejectOpen(false); setConfirmRejectTask(null); } }} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200">Cancelar</button>
              <button onClick={() => handleRejectTask(confirmRejectTask)} disabled={confirmRejectLoading} className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50">{confirmRejectLoading ? 'Rechazando...' : 'Rechazar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle de Proyecto */}
      {projectDetailOpen && projectDetail && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-50" onClick={() => setProjectDetailOpen(false)}>
          <div className="w-full max-w-3xl rounded-2xl overflow-hidden border border-slate-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-4 text-white flex items-center gap-3">
              <FaInfoCircle className="text-xl" />
              <div>
                <p className="text-xs/5 opacity-80">Detalle del Proyecto</p>
                <h3 className="text-lg font-bold">{projectDetail.nombre_proyecto}</h3>
              </div>
            </div>

            <div className="bg-white p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-500">Estado</p>
                  <div className="mt-2">
                    <StatusTag estado={projectDetail.estado || 'Pendiente'} />
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-500">Inicio</p>
                  <p className="mt-2 font-semibold text-slate-800 flex items-center gap-2">
                    <FaCalendar className="text-slate-400" />
                    {projectDetail.fecha_inicio ? formatDate(projectDetail.fecha_inicio) : 'Sin fecha'}
                  </p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-500">Término</p>
                  <p className="mt-2 font-semibold text-slate-800 flex items-center gap-2">
                    <FaCalendar className="text-slate-400" />
                    {projectDetail.fecha_fin ? formatDate(projectDetail.fecha_fin) : 'Sin fecha'}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-slate-800">Mis tareas en este proyecto</h4>
                  {Array.isArray(projectDetail.tareas_asignadas) && projectDetail.tareas_asignadas.length > 0 && (
                    <span className="bg-indigo-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      {projectDetail.tareas_asignadas.length}
                    </span>
                  )}
                </div>
                {Array.isArray(projectDetail.tareas_asignadas) && projectDetail.tareas_asignadas.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700">Nombre</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700">Estado</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-700">Fecha límite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectDetail.tareas_asignadas.map(t => (
                          <tr key={t.id_tarea} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 text-sm font-medium text-slate-800">{t.nombre}</td>
                            <td className="py-3 px-4"><StatusTag estado={t.estado} /></td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {t.fecha_termino ? formatDate(t.fecha_termino) : 'Sin fecha'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500">
                    <FaClipboardList className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No tienes tareas asignadas en este proyecto</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button 
                  onClick={() => setProjectDetailOpen(false)}
                  className="px-6 py-2 rounded-xl bg-slate-700 text-white font-semibold hover:bg-slate-800 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '—'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }
